import { DetailSurface, DetailFields } from "./detail-layout";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  App,
  Alert,
  Avatar,
  Button,
  Card,
  Empty,
  List,
  Space,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  BellOutlined,
  CheckOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { post, query } from "./api";
import { type Agent, type Notification, type Page, dateTime } from "./domain";
import { PasswordForm } from "./auth";
import { RefreshButton, Resource, SectionTitle, useResource } from "./shared";
import { notifyNotificationsChanged } from "./notifications-state";
export function Notifications({ unreadCount }: { unreadCount?: number }) {
  useEffect(() => { notifyNotificationsChanged(); }, []);
  const [page, setPage] = useState(1);
  const [unread, setUnread] = useState(false);
  const resource = useResource<Page<Notification & { feedback_id?: number }>>(
    `/notifications${query({ page, page_size: 10, unread: unread ? "true" : undefined })}`,
  );
  const { message } = App.useApp();
  const [busy, setBusy] = useState<number>();
  const [readingAll, setReadingAll] = useState(false);
  const markAllRead = async () => {
    setReadingAll(true);
    try {
      await post("/notifications/read-all");
      notifyNotificationsChanged();
      setPage(1);
      await resource.reload();
      message.success("全部消息已标记为已读");
    } catch (error) { message.error((error as Error).message); }
    finally { setReadingAll(false); }
  };
  const markRead = async (id: number) => {
    setBusy(id);
    try {
      await post(`/notifications/${id}/read`);
      notifyNotificationsChanged();
      if (unread && resource.data?.items.length === 1 && page > 1) setPage(page - 1);
      else await resource.reload();
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <>
      <SectionTitle
        eyebrow="MESSAGE CENTER"
        title="消息中心"
        description="任务审核、资金变动、结算与重要业务动态，及时掌握。"
        action={<Space wrap><Button icon={<CheckOutlined />} loading={readingAll} disabled={unreadCount === 0 || busy !== undefined} onClick={() => void markAllRead()}>全部标为已读</Button><RefreshButton onClick={() => { notifyNotificationsChanged(); return resource.reload(); }} /></Space>}
      />
      <Card>
        <Tabs
          activeKey={unread ? "unread" : "all"}
          onChange={(key) => {
            setUnread(key === "unread");
            setPage(1);
          }}
          items={[
            { key: "all", label: "全部消息" },
            { key: "unread", label: unreadCount == null ? "未读消息" : `未读消息 (${unreadCount})` },
          ]}
        />
        <Resource resource={resource}>
          {(data) => (
            <List
              dataSource={data.items}
              pagination={{
                current: page,
                pageSize: 10,
                total: data.total,
                onChange: setPage,
              }}
              locale={{
                emptyText: (
                  <Empty
                    description={unread ? "所有消息都已读完" : "暂无消息通知"}
                  />
                ),
              }}
              renderItem={(item) => (
                <List.Item
                  className={`merchant-notification ${!item.read_at ? "notification-unread" : ""}`}
                  actions={[
                    ...(item.feedback_id ? [<Link key="feedback" to={`/help/feedback/${item.feedback_id}`} onClick={() => { if (!item.read_at) void markRead(item.id); }}>查看反馈</Link>] : []),
                    ...(item.kind.startsWith("finance_") ? [<Link key="finance" to={(item.portal_task_id || item.task_id) ? `/finance?tab=tasks&task=${item.portal_task_id || item.task_id}` : "/finance"} onClick={() => { if (!item.read_at) void markRead(item.id); }}>查看资金记录</Link>] : []),
                    ...(!item.feedback_id && !item.kind.startsWith("finance_") && (item.portal_task_id || item.task_id)
                      ? [
                          <Link
                            key="task"
                            to={`/tasks/${item.portal_task_id || item.task_id}`}
                            onClick={() => { if (!item.read_at) void markRead(item.id); }}
                          >
                            查看任务
                          </Link>,
                        ]
                      : []),
                    ...(!item.read_at
                      ? [
                          <Button
                            key="read"
                            type="text"
                            icon={<CheckOutlined />}
                            loading={busy === item.id}
                            disabled={readingAll || (busy !== undefined && busy !== item.id)}
                            onClick={() => void markRead(item.id)}
                          >
                            标记已读
                          </Button>,
                        ]
                      : []),
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div
                        className={`notification-icon ${item.read_at ? "read" : ""}`}
                      >
                        <BellOutlined />
                      </div>
                    }
                    title={
                      <Space>
                        {item.title}
                        {!item.read_at && <span className="unread-dot" />}
                      </Space>
                    }
                    description={
                      <>
                        <p className="preserve-lines">{item.body}</p>
                        <small>{dateTime(item.created_at)}</small>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Resource>
      </Card>
    </>
  );
}
interface LoginLog {
  id: number;
  created_at: string;
  action?: string;
  success?: boolean;
  logged_in_at?: string;
}
export function Account({
  agent,
  onPasswordDone,
}: {
  agent: Agent;
  onPasswordDone: () => void;
}) {
  return (
    <DetailSurface className="merchant-account">
      <SectionTitle
        eyebrow="ACCOUNT & SECURITY"
        title="账号与安全"
        description="管理您的独立商户账号与登录安全。"
      />
      <div className="account-hero">
        <Avatar
          size={68}
          style={{ background: "#e7efff", color: "#376ac4", fontSize: 26 }}
        >
          {agent.name.slice(0, 1)}
        </Avatar>
        <div>
          <h2>{agent.name}</h2>
          <Space>
            <span className="muted">{agent.username}</span>
            <Tag color="success" bordered={false}>
              商户账号
            </Tag>
          </Space>
        </div>
        <div className="account-secure">
          <SafetyCertificateOutlined />
          <span>
            独立身份认证
            <br />
            <small>专属业务权限</small>
          </span>
        </div>
      </div>
      <Tabs
        items={[
          {
            key: "profile",
            label: (
              <>
                <UserOutlined /> 基本资料
              </>
            ),
            children: (
              <Card title="账号信息">
                <DetailFields
                  column={{ xs: 1, md: 2 }}
                  items={[
                    { key: "name", label: "商户名称", children: agent.name },
                    {
                      key: "username",
                      label: "登录账号",
                      children: agent.username,
                    },
                    {
                      key: "timezone",
                      label: "报表时区",
                      children: "北京时间（Asia/Shanghai）",
                    },
                    {
                      key: "business",
                      label: "业务状态",
                      children: agent.business_frozen ? (
                        <Tag color="warning">业务冻结</Tag>
                      ) : (
                        <Tag color="success">正常</Tag>
                      ),
                    },
                    {
                      key: "publishing",
                      label: "发布权限",
                      children: agent.publish_restricted ? (
                        <Tag color="warning">暂时受限</Tag>
                      ) : (
                        <Tag color="success">正常</Tag>
                      ),
                    },
                  ]}
                />
                <Alert
                  showIcon
                  type="info"
                  message="商户资料由平台统一维护。如需修改，请联系平台管理员。"
                  style={{ marginTop: 24 }}
                />
              </Card>
            ),
          },
          {
            key: "password",
            label: (
              <>
                <LockOutlined /> 修改密码
              </>
            ),
            children: (
              <Card title="登录密码">
                <PasswordForm onDone={onPasswordDone} />
              </Card>
            ),
          },
          {
            key: "logs",
            label: (
              <>
                <SafetyCertificateOutlined /> 登录记录
              </>
            ),
            children: <LoginLogs />,
          },
        ]}
      />
    </DetailSurface>
  );
}
function LoginLogs() {
  const [page, setPage] = useState(1);
  const resource = useResource<Page<LoginLog>>(
    `/auth/login-logs${query({ page, page_size: 10 })}`,
  );
  return (
    <Card
      title="最近登录记录"
      extra={<RefreshButton onClick={resource.reload} />}
    >
      <Resource resource={resource}>
        {(data) => (
          <Table
            rowKey="id"
            dataSource={data.items}
            columns={[
              {
                title: "登录时间（北京时间）",
                dataIndex: "created_at",
                render: (_v: string, r: LoginLog) =>
                  dateTime(r.logged_in_at || r.created_at),
              },
              {
                title: "登录结果",
                render: () => (
                  <Tag color="success" bordered={false}>
                    登录成功
                  </Tag>
                ),
              },
            ]}
            pagination={{
              current: page,
              pageSize: 10,
              total: data.total,
              onChange: setPage,
            }}
          />
        )}
      </Resource>
      <p className="source-note">仅展示当前账号的成功登录记录。</p>
    </Card>
  );
}
